import React, { memo, useEffect, useMemo } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { replaceNonAlpaNumeric } from '@/common/client';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import i18n from '@i18n';
import ArticleIcon from '@/icons/ArticleIcon';
import { navigate } from '@/routing';
import { useActiveRoute } from 'nike-ui/src/common/routing/NikeRouteContext';
import { LEGAL_INFO_ARTICLE, LEGAL_INFORMATION, SELF_EXCLUSION } from '@/routing/routes';
import BlockLoader from '@/ui/BlockLoader';
import ArticleBox from '@/article/views/ArticleBox';
import { loggedIn$ } from '@/account/subs';
import Markdown from 'nike-ui/src/components/markdown/Markdown';

const LegalInformation = memo(function LegalInformation({ articleUrl }) {
    const route = useActiveRoute();
    useEffect(() => {
        dispatch(['legalInfoArticles.load']);
    }, []);

    if (route === LEGAL_INFO_ARTICLE) return <LegalInfoArticle url={articleUrl} />;
    if (route === LEGAL_INFORMATION) return <LegalInfoMenu />;
});

const LegalInfoMenu = memo(function HelpMenu() {
    const { derefSub, subscribe } = useReframe();
    const menu = derefSub(['cms.section.menu', 'legalInfo']);
    const isLoggedOn = subscribe(loggedIn$);

    return (
        <div className="legal-info-menu grey">
            {menu ? (
                menu.entrySeq().map(([title, category]) => (
                    <div className="category" key={title}>
                        <h2 className="category-title">{title}</h2>
                        {category.map((article, index) => (
                            <ArticleBox
                                key={index}
                                route={LEGAL_INFO_ARTICLE}
                                title={article.get('title')}
                                articleUrl={article.get('url')}
                                event={{ category: 'Pravne informacie', action: article.get('url') }}
                            />
                        ))}
                        <ArticleBox
                            className={!isLoggedOn ? 'disabled' : ''}
                            route={SELF_EXCLUSION}
                            title={i18n('self-exclusion.menu-title')}
                            event={{ category: 'Pravne informacie', action: SELF_EXCLUSION.path }}
                        />
                    </div>
                ))
            ) : (
                <BlockLoader />
            )}
        </div>
    );
});

const LegalInfoArticle = memo(function LegalInfoArticle({ url }) {
    const canonUrl = useMemo(() => replaceNonAlpaNumeric(url), [url]);

    const derefSub = useReframe();
    const article = derefSub(['cms.section.article', 'legalInfo', canonUrl]);

    if (!article) {
        const menu = derefSub(['cms.section.menu', 'legalInfo']);
        return <EmptyHelpArticle menu={menu} />;
    }

    return <Markdown>{article.get('text')}</Markdown>;
});

function goToHelp() {
    navigate(LEGAL_INFORMATION);
}

function EmptyHelpArticle({ menu }) {
    if (!menu) {
        return <BlockLoader />;
    }

    return (
        <EmptyState
            stateIcon={<ArticleIcon width={40} height={40} fill="#aab2bd" opacity={1} />}
            buttonText={i18n('help.backtohelpmenu')}
            buttonClassName="blue-link home-page"
            buttonOnClick={goToHelp}
        />
    );
}

export default LegalInformation;
